import { useRouter } from 'next/compat/router';

export type IrlEventParams = {
  dispensaryId: string;
  enterpriseId: string;
  hasDispensaryParam: boolean;
  hasEnterpriseParams: boolean;
  isoRegionCode: string;
  irlEventId: string;
  isRouterReady: boolean;
  publicContextSessionId: string | null;
};

/** Extracts a single string value from a Next.js query param (string | string[] | undefined). */
function extractParam(param: string[] | string | undefined): string {
  return (Array.isArray(param) ? param[0] : param) ?? '';
}

/**
 * Extracts IRL event URL params from the router query.
 * Both route shapes are served by a single [...params] catch-all:
 *  - 2 segments → dispensary route: [dispensaryId, irlEventId]
 *  - 3 segments → enterprise route: [enterpriseId, isoRegionCode, irlEventId]
 */
export function useIrlEventParams(): IrlEventParams {
  const router = useRouter();
  const query = router?.query ?? {};
  const catchAllParams = Array.isArray(query.params) ? query.params : [];

  const isEnterpriseRoute = catchAllParams.length === 3;
  const isDispensaryRoute = catchAllParams.length === 2;

  const [enterpriseId = '', rawIsoRegionCode = '', enterpriseEventId = ''] = isEnterpriseRoute ? catchAllParams : [];
  const [dispensaryId = '', dispensaryEventId = ''] = isDispensaryRoute ? catchAllParams : [];

  const isoRegionCode = rawIsoRegionCode.toUpperCase();
  const irlEventId = enterpriseEventId || dispensaryEventId;

  return {
    dispensaryId,
    enterpriseId,
    hasDispensaryParam: !!dispensaryId,
    hasEnterpriseParams: !!enterpriseId && !!isoRegionCode,
    isoRegionCode,
    irlEventId,
    isRouterReady: router?.isReady ?? false,
    publicContextSessionId: extractParam(query['session-id']) || null,
  };
}
